import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import {
  getAdminNotificationSettings,
  updateAdminNotificationSettings,
  deleteAdminNotificationSettings,
  getNotificationStatus,
  sendTestNotification,
} from '../api';

export default function AdminNotifications() {
  const [admins, setAdmins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [newUserId, setNewUserId] = useState('');

  const flashSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAdminNotificationSettings();
      setAdmins(data.admins || []);
      // Categories come from the backend so this page cannot drift out of sync
      // with what the notification job actually checks.
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error loading notification settings:', err);
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await getNotificationStatus());
    } catch (err) {
      console.error('Error loading notification status:', err);
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadStatus();
  }, [loadSettings, loadStatus]);

  const handleToggle = async (userId, category, currentValue) => {
    try {
      setSaving(true);
      setError('');

      const admin = admins.find(a => a.userId === userId);
      const notifications = { ...(admin?.notifications || {}), [category]: !currentValue };

      const res = await updateAdminNotificationSettings(userId, notifications);
      setAdmins(prev => prev.map(a =>
        a.userId === userId ? { ...a, notifications, delivery: res?.delivery ?? a.delivery } : a
      ));
      flashSuccess('Settings updated');
    } catch (err) {
      console.error('Error updating notification settings:', err);
      setError(err?.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdmin = async () => {
    const userId = Number(newUserId);
    if (!userId || userId <= 0) {
      setError('Please enter a valid user ID');
      return;
    }
    if (admins.find(a => a.userId === userId)) {
      setError('This user is already in the admin list');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Enabled by default: the reason for adding someone is to alert them, and
      // an all-off default is how this list quietly stopped notifying anyone.
      const notifications = Object.fromEntries(categories.map(c => [c.key, true]));

      const res = await updateAdminNotificationSettings(userId, notifications);
      setAdmins(prev => [...prev, { userId, notifications, delivery: res?.delivery ?? null }]);
      setNewUserId('');
      flashSuccess(`Admin ${userId} added with all categories enabled`);
    } catch (err) {
      console.error('Error adding admin:', err);
      setError(err?.response?.data?.message || 'Failed to add admin');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm(`Remove user ${userId} from notifications?`)) return;
    try {
      setSaving(true);
      setError('');
      await deleteAdminNotificationSettings(userId);
      setAdmins(prev => prev.filter(a => a.userId !== userId));
      flashSuccess('Admin removed');
    } catch (err) {
      console.error('Error removing admin:', err);
      setError('Failed to remove admin');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (userId) => {
    try {
      setTestingId(userId);
      setError('');
      const res = await sendTestNotification(userId);
      flashSuccess(res?.message || `Test SMS sent to user ${userId}`);
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err?.response?.data?.message || 'Failed to send test notification');
    } finally {
      setTestingId(null);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error && !admins.length && !categories.length) return <ErrorBlock message={error} />;

  const failingChecks = (status?.checks || []).filter(c => !c.ok);

  return (
    <Box>
      <PageHeader title="Admin notifications" />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {/* Check health. A failing check is why alerts can look "quiet" while a
          queue is actually backing up, so it is surfaced before anything else. */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Check health</Typography>
          <Tooltip title="Re-run every check now">
            <IconButton onClick={loadStatus} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>

        {failingChecks.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{failingChecks.length} check{failingChecks.length > 1 ? 's are' : ' is'} failing</AlertTitle>
            No alerts can be sent for {failingChecks.length > 1 ? 'these categories' : 'this category'} until fixed.
          </Alert>
        )}

        {!status && <Typography variant="body2" color="text.secondary">Status unavailable.</Typography>}

        <Stack direction="row" flexWrap="wrap" gap={1}>
          {(status?.checks || []).map(c => {
            // A queue with items in it is waiting on someone, so it shows amber.
            // An event category (connections) has nothing to clear: its count is
            // how many happened lately, and a number there is not a warning.
            const waiting = c.kind !== 'event' && c.pending > 0;
            const summary = c.summary || `${c.pending} pending`;
            return (
              <Tooltip key={c.key} title={c.error || summary}>
                <Chip
                  icon={c.ok ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                  color={c.ok ? (waiting ? 'warning' : 'success') : 'error'}
                  label={c.ok ? `${c.label}: ${summary}` : `${c.label}: failing`}
                />
              </Tooltip>
            );
          })}
        </Stack>

        {status?.job && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Job {status.job.running ? 'running' : 'stopped'} · checking every {Math.round(status.job.intervalMs / 1000)}s
          </Typography>
        )}
      </Paper>

      {/* Add admin */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Add admin</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Bidsy user ID"
            size="small"
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
            disabled={saving}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAdmin}
            disabled={saving || !newUserId}
          >
            Add
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Alerts are sent by SMS to the phone number on the Bidsy account, so the user must have a
          valid phone number and must not have replied STOP.
        </Typography>
      </Paper>

      {/* Recipients */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              {categories.map(c => (
                <TableCell key={c.key} align="center">{c.label}</TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 && (
              <TableRow>
                <TableCell colSpan={categories.length + 2}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No admins configured — nobody is being alerted. Add a user ID above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {admins.map(admin => (
              <TableRow key={admin.userId}>
                <TableCell>
                  <Typography variant="body2">
                    {admin.delivery?.username || `User ${admin.userId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID {admin.userId}
                  </Typography>
                  {admin.delivery?.deliverable === false && (
                    <Chip
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                      label={admin.delivery.reason}
                    />
                  )}
                </TableCell>

                {categories.map(c => (
                  <TableCell key={c.key} align="center">
                    <Switch
                      checked={Boolean(admin.notifications?.[c.key])}
                      onChange={() => handleToggle(admin.userId, c.key, admin.notifications?.[c.key])}
                      disabled={saving}
                    />
                  </TableCell>
                ))}

                <TableCell align="right">
                  <Tooltip title="Send a real test SMS to this admin">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleTest(admin.userId)}
                        disabled={testingId === admin.userId || admin.delivery?.deliverable === false}
                      >
                        {testingId === admin.userId ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove admin">
                    <IconButton size="small" onClick={() => handleRemoveAdmin(admin.userId)} disabled={saving}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Each queue is checked on a timer and alerts once per new item. Items already pending when a
        category is first enabled are adopted silently, so switching one on does not replay the backlog.
        The two connection alerts are not queues: each connection is announced once when a fan requests
        it, and once when it is accepted.
      </Typography>
    </Box>
  );
}
